import { useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetTreatment, useSplitContext } from '@zg-rentals/split';

import SplitTestConstants from '../constants/SplitTestConstants';
import accessUtils from '../utils/accessUtils';
import { ModularListingContext } from '../containers/CreateListingPage/context';

/**
 * Determines access to ZRM tour scheduling posting path integration
 *
 * @returns { getTreatmentError, isGetTreatmentError, isGetTreatmentLoading, canAccessZRMTourScheduling }
 */
const useAccessPostingPathTourScheduling = () => {
  const {
    split: { experiments: splitExperiments },
  } = useSplitContext();

  const {
    // @ts-expect-error
    app: { cookieEnabledServices, appConfig },
    // @ts-expect-error
    properties: { currentProperty },
  } = useSelector((state) => state);

  const { listingAlias } = currentProperty || {};

  const { modularListingState } = useContext(ModularListingContext);
  const isInReactivationFlow = modularListingState?.isInReactivationFlow || false;

  /**
   * NOTE: must be called before `accessUtils` checks
   */
  const {
    data: getTreatmentData,
    error: getTreatmentError,
    isError: isGetTreatmentError,
    isFetching: isGetTreatmentFetching,
    isLoading: isGetTreatmentLoading,
    refetch: refetchGetTreatment,
  } = useGetTreatment(
    {
      featureName: SplitTestConstants.ZRM_TOUR_SCHEDULING_INTEGRATION,
      listingAlias,
    },
    {
      enabled: false,
    },
  );

  const splitExperimentsHasTourSchedulingProperty = splitExperiments.hasOwnProperty(
    SplitTestConstants.ZRM_TOUR_SCHEDULING_INTEGRATION,
  );

  useEffect(() => {
    if (!getTreatmentData && isGetTreatmentFetching === false && listingAlias) {
      refetchGetTreatment();
    }
  }, [getTreatmentData, isGetTreatmentFetching, refetchGetTreatment, listingAlias]);

  const canAccessPostingPathTourScheduling = useMemo(() => {
    if (
      isGetTreatmentLoading ||
      !getTreatmentData ||
      !splitExperimentsHasTourSchedulingProperty ||
      !currentProperty ||
      !appConfig
    ) {
      return false;
    }

    return accessUtils.isTourSchedulingEnabledForProperty({
      appConfig,
      cookieEnabledServices,
      currentProperty,
      isInReactivationFlow,
      splitExperiments,
    });
  }, [
    isGetTreatmentLoading,
    getTreatmentData,
    splitExperimentsHasTourSchedulingProperty,
    currentProperty,
    appConfig,
    cookieEnabledServices,
    isInReactivationFlow,
    splitExperiments,
  ]);

  return {
    getTreatmentError,
    isGetTreatmentError,
    isGetTreatmentLoading: isGetTreatmentLoading || !getTreatmentData,
    canAccessPostingPathTourScheduling,
  };
};

export default useAccessPostingPathTourScheduling;
